import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { IoPrintOutline } from 'react-icons/io5';
import { MdOutlinePerson, MdOutlinePersonOff } from 'react-icons/md';
import GaugeComponent from 'react-gauge-component';
import moment from 'moment';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Spinner from 'assets/gif/loading.gif';
import axios from 'config/axios';
import Header from 'components/Header';
import Button from 'components/Button';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import CategoryDetailLabel from 'components/CategoryDetailLabel';
import Table from 'components/Table';
import Dropdown from 'components/Dropdown2';
import Loader from 'components/Loader';
import ScoreWithScale from 'components/ScoreWithScale2';
import useWindowSize from 'hooks/useWindowSize';

import './index.css';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Dropdown4 from 'components/Dropdown4';
import QuestionPopup from 'components/QuestionPopup';

const timePerQuestionRW = 64 * 60 / 54;
const timePerQuestionMath = 70 * 60 / 44;
const CONFIGURATION= {
    'English Language Arts': 'ELA',
    'Mathematics': 'M'
}

function ClassSHSATReport () {
    const [isQuestionPopupOpen, setIsQuestionPopupOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [questionId, setQuestionId] = useState(null);
    const [correctStudent, setCorrectStudent] = useState(0);
    const [currentQuestionData, setCurrentQuestionData] = useState(null);
    const [testType, setTestType] = useState('');
    const [scoreChartData, setScoreChartData] = useState<{ range: string; students: number }[]>([]);
    const [testData, setTestData] = useState(null);
    const [modules, setModules] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [subjectIndex, setSubjectIndex] = useState(0);
    const [downloadInProgress, setDownloadInProgress] = useState(false);
    const [printInProgress, setPrintInProgress] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [hideStudentNames, setHideStudentNames] = useState(false);

    const windowSize = useWindowSize();
    const { classId, testId } = useParams();
    const printRef = useRef(null);
    const questionPopupRef = useOutsideClickHandler(() => handleExit(false));
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const pathParts = location.pathname.split('/');
        const testType = pathParts[2]; // ex: 'digital-act' or 'digital-sat'
        setTestType(testType);
    }, [location]);

    const exportStudentTestData = () => {
        setDownloadInProgress(true);
        axios({
            url: `tutors/report/download/${classId}/${testId}/`,
            method: 'GET',
            responseType: 'blob',
        })
        .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${testData?.className}-${testData?.testName}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setDownloadInProgress(false);
        })
        .catch(error => {
            console.error('Error downloading CSV:', error);
            setDownloadInProgress(false);
        });
    }

    const printBatchReport = () => {
        setPrintInProgress(true);
        axios.get(`tutors/report/print/${classId}/${testId}`)
            .then(res => {
                setReportData(res.data.studentTests);
                setPrintInProgress(false);
            })
            .catch(error => {
                console.log(error);
                setPrintInProgress(false);
            });

    }

    const handleQuestionPopup = (questionId: string) => {
        setQuestionId(questionId);
        axios.get(`tutors/classes/report/question/${questionId}/${testId}/`)
            .then(res => {
                setCurrentQuestionData(res.data)
            })
            .catch(error => {
                console.log(error);
            });

        setIsQuestionPopupOpen(true);
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const formatTime2 = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return hours 
            ? `${hours} hr ${minutes} min ${seconds} sec`
            : minutes
            ? `${minutes} min ${seconds} sec`
            : `${seconds} sec`;
    }

    // Print the live DOM natively (the @media print stylesheet isolates the
    // batch report) instead of cloning via react-to-print.
    const handlePrint = () => {
        // Scope print isolation to this page only: the @media print rules key
        // off this body class, so other pages' printing is never affected.
        document.body.classList.add('printing-shsat-class-report');
        // Remove the browser's default page margins for this print only, which
        // also drops the auto-added header/footer (date, title, URL, page #).
        // Injected here and removed on afterprint so other prints keep theirs.
        const pageStyle = document.createElement('style');
        pageStyle.textContent = '@page { margin: 0; }';
        document.head.appendChild(pageStyle);
        const cleanup = () => {
            document.body.classList.remove('printing-shsat-class-report');
            pageStyle.remove();
            window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        window.print();
    };

    const getScoreDistribution = (testSections, studentScores) => {
        let scoreRanges = [];
        const validScores = studentScores.filter(score => score !== null && score !== undefined);
    
        if (testSections === 2) {
            // SHSAT Total (0~114)
            scoreRanges = [
                { label: '< 60', min: 0, max: 59 },
                { label: '60 - 69', min: 60, max: 69 },
                { label: '70 - 79', min: 70, max: 79 },
                { label: '80 - 89', min: 80, max: 89 },
                { label: '90 - 99', min: 90, max: 99 },
                { label: '100 - 113', min: 100, max: 113 },
                { label: "114", min: 114, max: 114 }
            ];
        } else if (testSections=== 1) {
            // SHSAT Section (0~57)
            scoreRanges = [
                { label: '< 20', min: 0, max: 19 },
                { label: '20 - 29', min: 20, max: 29 },
                { label: '30 - 39', min: 30, max: 39 },
                { label: '40 - 49', min: 40, max: 49 },
                { label: '50 - 56', min: 50, max: 56 }, 
                { label: '57', min: 57, max: 57 },
            ];
        }
    
        // bins 초기화
        const bins = scoreRanges.map(range => ({
            range: range.label,
            students: 0,
        }));
    
        validScores.forEach(score => {
            for (let i = 0; i < scoreRanges.length; i++) {
                const { min, max } = scoreRanges[i];
                if (score >= min && score < max) {
                    bins[i].students += 1;
                    break;
                }
            }
        });
    
        return bins;
    };

    const handleExit = (isExit: boolean) => {
        setIsQuestionPopupOpen(isExit);
        setCurrentQuestionData(null);
    }

    useEffect(()=>{
        if (testData && questionId) {
            let correctCount = 0;
            let taken = 0;

            testData.students.forEach(student => {
                student.moduleAnswers.forEach(moduleAnswer => {
                    const answer = moduleAnswer.answers.find(a => a.questionId === questionId);
                    if (answer) {
                        taken +=1;
                        if (answer.isCorrect) {
                            correctCount += 1;
                        }
                    }
                });
            });

            setCorrectStudent(taken>0? (correctCount/taken)*100: null);
        }
    }, [testData, questionId]);

    useEffect(() => {
        if (!testType) return;

        axios.get(`tutors/classes/${classId}/${testType}/report/${testId}/`)
            .then(res => {
                setTestData(res.data);
                let modulesArray = [];
                let subjectsArray = [];
                for (let module of res.data.modules) {
                    modulesArray.push(module.subject)
                }
                setModules(modulesArray);
                if (!res.data.isConfigured) {
                    subjectsArray.push('Total')
                }
                for (let subject of res.data.configTestSections) {
                    subjectsArray.push(subject)
                }
                setSubjects(subjectsArray);
                const firstSubject = subjectsArray[0];
                let data;
                if (firstSubject==='Total') {
                    data = getScoreDistribution(
                        2,
                        res.data.studentScores
                    );
                } else if (firstSubject==='English Language Arts') {
                    data = getScoreDistribution(
                        1,
                        res.data.studentEnglishScores
                    );
                } else if (firstSubject==='Mathematics') {
                    data = getScoreDistribution(
                        1,
                        res.data.studentMathScores
                    );
                }
                setScoreChartData(data)
            })
            .catch(error => {
                console.log(error);
            });
    }, [testType]);

    useEffect(()=>{
        const selected = subjects[subjectIndex]
        let data;
        if (selected==='Total') {
            data = getScoreDistribution(
                2,
                testData.studentScores
            );
        } else if (selected==='English Language Arts') {
            data = getScoreDistribution(
                1,
                testData.studentEnglishScores
            );
        } else if (selected==='Mathematics') {
            data = getScoreDistribution(
                1,
                testData.studentMathScores
            );
        }
        setScoreChartData(data)
    }, [subjectIndex]);

    useEffect(() => {
        if (reportData) {
            handlePrint();
        }
    }, [reportData]);

    if (testData === null) return <Loader />;

    return (
        <div className='classSHSATReport'>
            <Helmet>
                <title>
                    Class Report - Test Center Admin
                </title>
            </Helmet>
            {isQuestionPopupOpen &&
                <QuestionPopup
                    info={currentQuestionData?.info}
                    correctStudent={correctStudent!==null? `${correctStudent.toFixed(1)}%`: '-'}
                    testName={testData?.testName}
                    question={currentQuestionData?.question}
                    passages={currentQuestionData?.passages}
                    figures={currentQuestionData?.figures}
                    choices={currentQuestionData?.choices}
                    hotText={currentQuestionData?.hotText}
                    buckets={currentQuestionData?.buckets}
                    dragAndDropItems={currentQuestionData?.dragAndDropItems}
                    tableGridRows={currentQuestionData?.tableGridRows}
                    tableGridColumns={currentQuestionData?.tableGridColumns}
                    inlineChoices={currentQuestionData?.inlineChoices}
                    questionHelper={currentQuestionData?.questionHelper}
                    correctChoice={currentQuestionData?.correctChoice}
                    correctAnswers={currentQuestionData?.correctAnswers}
                    manageExit={() => handleExit(false)}
                    innerRef={questionPopupRef}
                />
            }
            <Header
                title={(testData?.testName && testData?.configTestSections?.length>0)? (testData?.configTestSections?.length==1?`${testData.testName} (${testData.configTestSections.map(section=> CONFIGURATION[section]).join(', ')})`:`${testData.testName}`) : null}
                prevRoutes={testData && [{ name: testData.className, path: `/classes/${classId}` }]}
            />
            <div className='classSHSATReport__subHeaderRow'>
                <h2 className='classSHSATReport__subHeader'>
                    Scores by students
                </h2>
                <div className='classSHSATReport__buttonGroup'>
                    <Button className='classSHSATReport__button' secondary onClick={() => setHideStudentNames(!hideStudentNames)}>
                        {hideStudentNames
                        ?   <>
                                <MdOutlinePerson className='classSHSATReport__buttonIcon' />
                                <p>Show</p>
                            </>
                        :   <>
                                <MdOutlinePersonOff className='classSHSATReport__buttonIcon' />
                                <p>Hide</p>
                            </>
                        }
                    </Button>
                    <Button className='classSHSATReport__button' onClick={printBatchReport}>
                        {printInProgress
                        ?   <img src={Spinner} />
                        :   <>
                                <IoPrintOutline className='classSHSATReport__buttonIcon' />
                                <p>Report</p>                            
                            </>
                        }
                    </Button>
                </div>
            </div>
            <div className='classSHSATReport__scoresContainer'>
                <Table
                    className='classSHSATReport__table'
                    header={[
                        'Students',
                        ...testData.classTestSections.map((classTestSection, p) => (
                            <p className='classSHSATReport__cellCenter'>
                                {classTestSection}
                            </p>
                        )),
                        <p className='classSHSATReport__cellCenter'>Score</p>,
                        <p className='classSHSATReport__cellCenter'>Progress</p>
                    ]}
                    columnTypes={[
                        'string',
                        ...Array(testData.classTestSections.length).fill('number'),
                        'number',
                        null
                      ]}
                    rows={testData.students.map(student => {
                        // const firstScoreIndex = student.testScores.findIndex(score => score !== null);
                        // const lastScoreIndex = student.testScores.findLastIndex(score => score !== null);
                        // const progress = 100*(student.testScores[lastScoreIndex]-student.testScores[firstScoreIndex])/student.testScores[firstScoreIndex]

                        return [
                            hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                            ...testData.classTestSections.map((subjectName) => (
                                (() => {
                                    const testSection = student.test?.testSections.find(
                                    (section) => section.subject === subjectName
                                    );
                                    // RAW correct count shown as "X / N" (0 is valid, so check != null)
                                    return testSection?.score != null
                                        ? `${Math.floor(testSection.score)}${testSection.questionCount != null ? ` / ${testSection.questionCount}` : ''}`
                                        : null;
                                })()
                            )),
                            student.test.score != null
                                ? `${Math.floor(student.test.score)}${student.test.questionCount != null ? ` / ${student.test.questionCount}` : ''}`
                                : null,
                            <div className='classSHSATReport__progress classSHSATReport__cellCenter'>
                                {new Array(student.test.testModuleCount).fill(null).map((_count, c) => (
                                    <div className={student.test.completedModuleCount > c
                                        ? 'classSHSATReport__progressBar--filled'
                                        : (student.test.inProgress && c === 0)
                                        ? 'classSHSATReport__progressBar--started'
                                        : 'classSHSATReport__progressBar'
                                    }></div>
                                ))}
                            </div>
                        ];
                    })}
                    onSelectCell={testData.students.map(student => ([
                        hideStudentNames ? null : () => navigate(`/students/${student.studentId}`),
                        ...testData.classTestSections.map(() => null),
                        student.test.score? () => navigate(`/report/digital-shsat/${student.test.testId}`): null,
                        null
                    ]))}
                    navigator={false}
                />
                <div className='classSHSATReport__scoreDistribution'>
                    <div className='classSHSATReport__scoreDistribution__header'>
                        <div className='classSHSATReport__scoreDistribution__title'>Score distribution</div>
                        <Dropdown4
                            width={210}
                            className='classSHSATReport__dropdown'
                            data={subjects}
                            onSelect={value => setSubjectIndex(subjects.findIndex(module => module === value))}
                            value={subjects[subjectIndex]}
                        />
                    </div>
                    <div className='classSHSATReport__scoreDistribution__content'>
                        <div className='classSHSATReport__scoreDistribution__graph'>
                            <ResponsiveContainer>
                                <BarChart data={scoreChartData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                                    <XAxis
                                    dataKey="range"
                                    tick={{ fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                    label={{
                                        value: 'Score',
                                        position: 'insideBottom',
                                        offset: -10,
                                        style: {
                                        fontSize: 12,
                                        fill: '#6B7280',
                                        fontWeight: 500,
                                        },
                                    }}
                                    />
                                    <YAxis
                                    allowDecimals={false}
                                    domain={[0, 'dataMax + 1']}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 12 }}
                                    />
                                    <Tooltip />
                                    <Bar dataKey="students" fill="#4F46E5" radius={[4, 4, 0, 0]} minPointSize={2}/>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className='classSHSATReport__scoreDistribution__avgScore'>
                            <div className='classSHSATReport__scoreDistribution__avg'>Avg Score</div>
                            <div className='classSHSATReport__scoreDistribution__avgScoreNum'>{testData?.avgScore}</div>
                        </div>
                    </div>
                </div>
                {/* <div className='classSHSATReport__category'>
                    <div className='classSHSATReport__scoreDistribution__title'>Category breakdown (%correct)</div>
                    <div className='classSHSATReport__scoreDistribution__analysisWrap'>
                        {Object.keys(testData.avgCorrectPercent).length > 0 ? (
                            Object.entries(testData.avgCorrectPercent).map(([subject, categories], index) => (
                                <div key={index} className='classSHSATReport__analysisBox'>
                                    <div className='classSHSATReport__analysisBoxTitle'>{subject}</div>
                                    <div className='classSHSATReport__analysisContentWrap'>
                                        {Object.entries(categories)?.map(([categoryName, answerNumInfo], i) => {
                                            const { numCorrect, numQuestions } = answerNumInfo;
                                            const percent = numQuestions > 0 ? (numCorrect / numQuestions) * 100 : 0;
                                            return (
                                                <div key={i} className='classSHSATReport__categoryWrap'>
                                                    <CategoryDetailLabel
                                                        name={categoryName}
                                                        categories={answerNumInfo.categories}
                                                        active={activeCategory === `${subject}|${categoryName}`}
                                                        onToggle={() => setActiveCategory(activeCategory === `${subject}|${categoryName}` ? null : `${subject}|${categoryName}`)}
                                                        onClose={() => setActiveCategory(null)}
                                                    />
                                                    <LevelBar className={subject==='Reading and Writing'
                                                                        ? 'LevelBar__greenBox'
                                                                        : 'LevelBar__orangeBox'
                                                                        } 
                                                                percent = {percent}/>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        ): (
                            <div className='classSHSATReport__analysisBox'>
                                <div className='classSHSATReport__analysisBoxTitle'>-</div>
                                <div className='classSHSATReport__analysisContentWrap'>
                                <div className='classSHSATReport__categoryWrap'>
                                    <div className='classSHSATReport__categoryTitle'>-</div>
                                    <LevelBar className='LevelBar__defaultBox' percent={0} />
                                </div>
                                </div>
                            </div>
                            )
                        }
                    </div>
                </div> */}
            </div>
            <div className='classSHSATReport__subHeaderRow'>
                <h2 className='classSHSATReport__subHeader'>
                    Student responses
                </h2>
                <Dropdown
                    className='classSHSATReport__dropdown'
                    data={modules}
                    onSelect={value => setModuleIndex(modules.findIndex(module => module === value))}
                    value={modules[moduleIndex]}
                />
                <div className='classSHSATReport__buttonGroup'>
                    {/* <Button className='classSHSATReport__button' onClick={printBatchReport}>
                        {printInProgress ? <img src={Spinner} /> : 'Print'}
                    </Button> */}
                    <Button className='classSHSATReport__button' onClick={exportStudentTestData}>
                        {downloadInProgress ? <img src={Spinner} /> : 'Download'}
                    </Button>
                </div>
            </div>
            <Table
                header={['Students', ...testData.modules[moduleIndex].questions.map((question, q) => (
                    // <p className={`
                    //     classSHSATReport__cellCenter
                    //     classSHSATReport__response 
                    //     classSHSATReport__cursor
                    //     ${testData.students.filter(student => (
                    //         student.moduleAnswers.find(moduleAnswer => (
                    //             moduleAnswer.subject === testData.modules[moduleIndex].subject
                    //             && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                    //             && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                    //         ))
                    //     )).find(student => (
                    //         !student.moduleAnswers.find(moduleAnswer => (
                    //             moduleAnswer.subject === testData.modules[moduleIndex].subject
                    //             && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                    //             && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                    //         )).answers[q].isCorrect
                    //     )) && 'classSHSATReport__cellWrong'}
                    // `}
                    <p className={`
                        classSHSATReport__cellCenter
                        classSHSATReport__response 
                        classSHSATReport__cursor
                        classSHSATReport__tableHeader
                    `}
                    onClick={() => handleQuestionPopup(question[1])}>
                        Q{question[0]}
                    </p>
                ))]}
                rows={[
                    [
                        <p className='classSHSATReport__header'># Incorrect</p>,
                        ...testData.modules[moduleIndex].questions.map((_question, q) => (
                            <p className='classSHSATReport__header classSHSATReport__cellCenter'>
                                {testData.students.filter(student => (
                                    student.moduleAnswers.find(moduleAnswer => (
                                        moduleAnswer.subject === testData.modules[moduleIndex].subject
                                        && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                                    ))
                                )).filter(student => (
                                    !student.moduleAnswers.find(moduleAnswer => (
                                        moduleAnswer.subject === testData.modules[moduleIndex].subject
                                        && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                                    ))?.answers?.[q]?.isCorrect
                                )).length
                            }</p>
                        ))
                    ],
                    ...testData.students.filter(student => (
                        student.moduleAnswers.find(moduleAnswer => (
                            moduleAnswer.subject === testData.modules[moduleIndex].subject
                            && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                        ))
                    )).map(student => ([
                        hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                        ...(student.moduleAnswers.find(moduleAnswer => (
                            moduleAnswer.subject === testData.modules[moduleIndex].subject
                            && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                        ))?.answers ?? []).map(answer => (
                            <p className={`classSHSATReport__cellCenter classSHSATReport__response ${!answer?.isCorrect && ((answer?.answer && answer?.answer?.length > 0)
                                ? 'classSHSATReport__cellWrong'
                                : 'classSHSATReport__cellSkipped'
                            )}`}>
                                {answer?.questionType === 1 || answer?.questionType === 3
                                ? Array.isArray(answer?.answer)
                                    ? answer.answer.length > 0? answer.answer.join(', ') : '-'
                                    : answer?.answer? answer.answer : '-'
                                : '-'
                                }
                            </p>
                        ))
                    ])),
                ]}
                navigator={false}
            />
            {reportData &&
                <div className='classSHSATReport__report' ref={printRef}>
                    {reportData.map(testData => (<>
                        <div className='classSHSATReportPrint__headerGroup'>
                            <div className='classSHSATReportPrint__headerSection'>
                                {testData.logo && 
                                    <img
                                        className='classSHSATReportPrint__headerLogo'
                                        alt='logo'
                                        src={testData.logo}
                                    />
                                }
                            </div>
                            <div className='classSHSATReportPrint__headerSection'>
                                <div className='classSHSATReportPrint__headerInfo'>
                                    <p>
                                        Name: {testData.studentName}
                                    </p>
                                    <p>
                                        Email: {testData.email}
                                    </p>
                                    <p>
                                        Test name: {testData.name}
                                    </p>
                                    <p>
                                        Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className='classSHSATReportPrint__headerGroup'>
                            <h1 className='classSHSATReportPrint__headerTitle'>
                                Test Result Overview
                            </h1>
                            <div className='classSHSATReportPrint__buttonGroup'>
                                <Button className='classSHSATReportPrint__button' secondary onClick={handlePrint}>
                                    PRINT REPORT
                                </Button>
                                <ButtonLink className='classSHSATReportPrint__button' to={`/review/${testId}`}>
                                    REVIEW QUESTIONS
                                </ButtonLink>
                            </div>
                        </div>
                        <div className='classSHSATReportPrint__card'>
                            <div className='classSHSATReportPrint__cardRow'>
                                <div className='classSHSATReportPrint__overview'>
                                    <div className='classSHSATReportPrint__overviewScore'>
                                        <div className='classSHSATReportPrint__overviewScores'>
                                            <h2 className='classSHSATReportPrint__scoreTitle'>TOTAL SCORE</h2>
                                            <div className='classSHSATReportPrint__scorePercent'>
                                                {testData.score != null ? (<>
                                                    {testData.score}
                                                    <span style={{ fontSize: '0.5em', fontWeight: 400 }}>/{testData.totalQuestionCount}</span>
                                                </>) : '-'}
                                            </div>
                                        </div>
                                        <div className='classSHSATReportPrint__scoreDivider'></div>
                                    </div>
                                    {testData.testSections.map(testSection => (
                                        <div className='classSHSATReportPrint__sectionScoreBox'>
                                            <div className='classSHSATReportPrint__sectionScores'>
                                                <h3 className='classSHSATReportPrint__sectionScoreTitle'>
                                                    {testSection.subject}
                                                </h3>
                                                <div className='classSHSATReportPrint__scorePercent'>
                                                    {testSection.score != null ? (<>
                                                        {testSection.score}
                                                        <span style={{ fontSize: '0.5em', fontWeight: 400 }}>/{testSection.questionCount}</span>
                                                    </>) : '-'}
                                                </div>
                                            </div>
                                            <div className='classSHSATReportPrint__rawScore'>
                                                <div className='classSHSATReportPrint__overviewRow'>
                                                    <div className='classSHSATReportPrint__overviewItem'>
                                                        <table className='classSHSATReportPrint__overviewTable'>
                                                            <tbody>
                                                                <tr>
                                                                    <th></th>
                                                                    <th>Correct</th>
                                                                    <th>Time</th>
                                                                </tr>
                                                                <tr>
                                                                    <td>Total</td>
                                                                    <td>{testData.testModules.filter(testModule => (
                                                                        testModule.subject === testSection.subject
                                                                    )).reduce((total, testModule) => (
                                                                        total + testModule.answers.filter(answer => answer.isCorrect).length
                                                                    ), 0)}/{testData.testModules.filter(testModule => (
                                                                        testModule.subject === testSection.subject
                                                                    )).reduce((total, testModule) => (
                                                                        total + testModule.answers.length
                                                                    ), 0)}</td>
                                                                    <td>{formatTime(testData.testModules.filter(testModule => (
                                                                        testModule.subject === testSection.subject
                                                                    )).reduce((total, testModule) => (
                                                                        total + testModule.answers.reduce((time, answer) => (
                                                                            time + answer.timeSpent
                                                                        ), 0)
                                                                    ), 0))}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {testData.testSections.some(s => s.rootCategories && s.rootCategories.length > 0) && (<>
                            <h2 className='classSHSATReportPrint__cardSubTitle'>
                                Category Scores
                            </h2>
                            <div className='classSHSATReportPrint__overviewRow'>
                                {testData.testSections.map((testSection, s) => (
                                    <div className='classSHSATReportPrint__overviewItem'>
                                        <h4 className='classSHSATReportPrint__overviewLabel'>
                                            {testSection.subject}
                                        </h4>
                                        {(testSection.rootCategories ?? []).map(rootCategory => (<>
                                            <h5 className='classSHSATReportPrint__levelLabel'>
                                                {rootCategory.name}
                                            </h5>
                                            <LevelBar
                                                className={s % 2 === 0 ? 'classSHSATReportPrint__levelGreen' : 'classSHSATReportPrint__levelOrange'}
                                                percent={rootCategory.numQuestions ? 100*rootCategory.numCorrect/rootCategory.numQuestions : 0}
                                                label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                            />
                                            {/* Level-2 sub-skills are deliberately NOT rendered here.
                                                Every other SHSAT report surface -- the tutor's
                                                single-student report, the Students-page bulk print and
                                                the student's own report -- keeps them in a
                                                click/hover popover that no print stylesheet reveals, so
                                                they never reach paper there. Showing them only in the
                                                class batch print made the same attempt print two
                                                different levels of detail depending on which button you
                                                pressed. The root-category bars above carry the same
                                                totals. `rootCategory.categories` is still returned by
                                                the API, so this is a render choice, not a data loss --
                                                see PR #207. */}
                                        </>))}
                                    </div>
                                ))}
                            </div>
                            </>)}
                            {/* <h2 className='classSHSATReportPrint__cardSubTitle--centered'>
                                Section Time Averages
                            </h2>
                            <div className='classSHSATReportPrint__overviewRow'>
                                {testData.testSections.map(testSection => (
                                    <div className='classSHSATReportPrint__overviewItem'>
                                        <h4 className='classSHSATReportPrint__overviewLabel--centered'>
                                            {testSection.subject.replace('ACT ', '')}
                                        </h4>
                                        <table className='classSHSATReportPrint__timeTable'>
                                            <tbody>
                                                <tr>
                                                    <th></th>
                                                    <th>Section Time</th>
                                                    <th>Time Per Question</th>
                                                </tr>
                                                {testSection.rootCategories.map(rootCategory => (
                                                    <tr>
                                                        <td>
                                                            {rootCategory.name}
                                                        </td>
                                                        <td>
                                                            {formatTime2(rootCategory.timeSpent)}
                                                        </td>
                                                        <td>
                                                            <GaugeComponent
                                                                className='classSHSATReportPrint__timeGauge'
                                                                type='semicircle'
                                                                value={rootCategory.timeSpent/rootCategory.numQuestions}
                                                                minValue={0}
                                                                maxValue={testSection.subject === 'Reading and Writing'
                                                                    ?   timePerQuestionRW * 3
                                                                    :   testSection.subject === 'Math'
                                                                    ?   timePerQuestionMath * 3
                                                                    :   180
                                                                }
                                                                arc={{
                                                                    'subArcs': [
                                                                        { length: 0.33, color: "#5BE12C"},
                                                                        { length: 0.33, color: "#F5CD19"},
                                                                        { length: 0.33, color: "#EA4228"}
                                                                    ]
                                                                }}
                                                                // style={{ 'width': '200px' }}
                                                                pointer={{
                                                                    'width': 10,
                                                                }}
                                                                labels={{
                                                                    'valueLabel': {
                                                                        // 'formatTextValue': (value) => formatTime(value),
                                                                        // 'style': {
                                                                        //     'fontSize': '40px',
                                                                        //     'fill': '#000',
                                                                        //     'textShadow': null,
                                                                        //     'marginTop': 20
                                                                        // }
                                                                        'hide': true
                                                                    },
                                                                    'tickLabels': {
                                                                        'hideMinMax': true
                                                                    }
                                                                }}
                                                            />
                                                            {formatTime2(rootCategory.timeSpent/rootCategory.numQuestions)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}
                            </div> */}
                        </div>
                        <div className='classSHSATReportPrint__printBreak'></div>
                    </>))}
                </div>
            }
        </div>
    );
}

export default ClassSHSATReport;